import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { IKart, IKartTuru } from '@/src/service/fikoyt-ui/interfaces/IKart.interface';
import { UnifiedCacheService } from '@/src/app/shared/services';
import {
  AkrabalikTuru, AkrabalikBilgi, AkrabalikOnayDurumu, IAileBagi,
  IAkrabalikAdim, IAkrabalikYolu, IElleGirilenKisi, IFamilyChartDugum,
  IAileIstatistikleri, IZamanCizelgesiOlayi, YasamOlayiTuru,
  ISoyAgaciKisiVeri, bagdanTurCozumle, cinsiyetleTurDuzelt
} from '../interfaces/soy-agaci.interface';
import { escapeRegex } from '../../yuz-tanima/utils/regex.utils';

@Injectable({ providedIn: 'root' })
export class SoyAgaciService {

  private FikoYT = inject(FikoYTService);
  private uc = inject(UnifiedCacheService);

  /** Context menu event - window global yerine service Subject */
  contextMenuAc$ = new Subject<{ kisiId: string; event: MouseEvent }>();

  /** Ağaç açıldığında odaklanılacak kişi ID'si (bir kez kullanılır) */
  bekleyenOdaklaId: string | null = null;

  // =====================================================
  // MERKEZI SORGU YARDIMCILARI
  // =====================================================

  /** Aggregate pipeline calistir ve sonucu dondur */
  async aggregateSorgu(pipeline: any[]): Promise<any[]> {
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
    istek.parametreler = { aggregate: pipeline };
    await istek.isteSync();
    return istek.cevap?.sonuc || [];
  }

  /** Guncelle sorgusu calistir */
  async guncelleSorgu(filter: any, set: any, multi = false): Promise<void> {
    const istek = new FikoYTIstekKok({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.guncelle',
      onEkOlsun: true,
      depoModu: false,
      oncelik: 0,
    });
    istek.parametreler = { filter, set, upsert: false, multi };
    await istek.isteSync();
    const hata = istek.cevap?.durum?.hata;
    if (hata) throw new Error(String(hata));
  }

  // =====================================================
  // CACHE MEKANIZMASI - UnifiedCacheService delegate
  // =====================================================

  private readonly CACHE_ONEK = 'soyagaci-kisi-';
  private readonly CACHE_TTL = 60_000;

  cacheSifirla() {
    this.uc.temizle(this.CACHE_ONEK);
  }

  cachedenSil(kisiId: string) {
    this.uc.sil(`${this.CACHE_ONEK}${kisiId}`);
  }

  /** Aktif kullanicinin tum onaylanmis aile baglarini cek (bidirectional) */
  tumAgaciPipelineOlustur(kullaniciId: string): any[] {
    return [
      // 1. Kullanicinin TUM baglarini bul (kisi1 VEYA kisi2 olarak)
      {
        $match: {
          vt: "FikoYT.sistem.aileBaglari",
          silindi: false,
          "veri.onayDurumu": AkrabalikOnayDurumu.ONAYLANDI,
          $or: [
            { "veri.kisi1": kullaniciId },
            { "veri.kisi2": kullaniciId }
          ]
        }
      },
      // 2. kisi2 uzerinden ileriye dogru tara
      {
        $graphLookup: {
          from: "hareketler",
          startWith: "$veri.kisi2",
          connectFromField: "veri.kisi2",
          connectToField: "veri.kisi1",
          as: "agacDallari",
          maxDepth: 10,
          depthField: "derinlik",
          restrictSearchWithMatch: {
            vt: "FikoYT.sistem.aileBaglari",
            silindi: false,
            "veri.onayDurumu": AkrabalikOnayDurumu.ONAYLANDI
          }
        }
      },
      // 3. kisi1 uzerinden geriye dogru tara
      {
        $graphLookup: {
          from: "hareketler",
          startWith: "$veri.kisi1",
          connectFromField: "veri.kisi1",
          connectToField: "veri.kisi2",
          as: "agacDallariTers",
          maxDepth: 10,
          depthField: "derinlik",
          restrictSearchWithMatch: {
            vt: "FikoYT.sistem.aileBaglari",
            silindi: false,
            "veri.onayDurumu": AkrabalikOnayDurumu.ONAYLANDI
          }
        }
      },
      // 4. kisi2 kullanici bilgilerini cek (sadece gerekli alanlar)
      {
        $lookup: {
          from: "goUyeler",
          localField: "veri.kisi2",
          foreignField: "_id",
          as: "kisi2Bilgileri",
          pipeline: [{ $project: { isim: 1, soyIsim: 1, ad: 1, soyad: 1, cinsiyet: 1, profilResmi: 1 } }]
        }
      },
      // 5. kisi1 kullanici bilgilerini de cek (sadece gerekli alanlar)
      {
        $lookup: {
          from: "goUyeler",
          localField: "veri.kisi1",
          foreignField: "_id",
          as: "kisi1Bilgileri",
          pipeline: [{ $project: { isim: 1, soyIsim: 1, ad: 1, soyad: 1, cinsiyet: 1, profilResmi: 1 } }]
        }
      }
    ];
  }

  /** Bulunan kisilerin TUM baglarini cek (iki asamali yukleme icin) */
  tamamlayiciBaglarPipelineOlustur(kisiIdleri: string[]): any[] {
    return [
      {
        $match: {
          vt: "FikoYT.sistem.aileBaglari",
          silindi: false,
          "veri.onayDurumu": AkrabalikOnayDurumu.ONAYLANDI,
          $or: [
            { "veri.kisi1": { $in: kisiIdleri } },
            { "veri.kisi2": { $in: kisiIdleri } }
          ]
        }
      },
      // Sadece gerekli bag alanlarini cek
      {
        $project: {
          'veri.kisi1': 1, 'veri.kisi2': 1,
          'veri.akrabalikTuru': 1, 'veri.tersAkrabalikTuru': 1,
          'veri.onayDurumu': 1, 'veri.elleGirilenKisi': 1,
        }
      },
      {
        $lookup: {
          from: "goUyeler",
          localField: "veri.kisi2",
          foreignField: "_id",
          as: "kisi2Bilgileri",
          pipeline: [{ $project: { isim: 1, soyIsim: 1, ad: 1, soyad: 1, cinsiyet: 1 } }]
        }
      },
      {
        $lookup: {
          from: "goUyeler",
          localField: "veri.kisi1",
          foreignField: "_id",
          as: "kisi1Bilgileri",
          pipeline: [{ $project: { isim: 1, soyIsim: 1, ad: 1, soyad: 1, cinsiyet: 1 } }]
        }
      }
    ];
  }

  /** Elle girilmis kisileri bul (iliskilendirme icin) - soyAgaciKisiler'den */
  elleGirilmisKisilerPipeline(arama = '', sayfa = 0, sayfaBoyutu = 10): any[] {
    const sahipId = this.FikoYT.kimlik.aktifKullaniciJson()?._id;
    const pipeline: any[] = [
      {
        $match: {
          vt: 'FikoYT.sistem.soyAgaciKisiler',
          silindi: false,
          ...(sahipId ? { sahip: sahipId } : {}),
        }
      }
    ];
    if (arama) {
      pipeline.push({
        $match: {
          $or: [
            { 'veri.isim': { $regex: escapeRegex(arama), $options: 'i' } },
            { 'veri.soyIsim': { $regex: escapeRegex(arama), $options: 'i' } },
          ]
        }
      });
    }
    // YENİ EKLENEN AŞAMA: Mantıksal olarak benzersiz kişileri bulmak için gruplama
    pipeline.push({
      $group: {
        _id: { isim: '$veri.isim', soyIsim: '$veri.soyIsim', cinsiyet: '$veri.cinsiyet' }, // İsim, soyisim ve cinsiyete göre grupla
        ilkKayit: { $first: '$$ROOT' } // Her grubun ilk kaydını al
      }
    });
    pipeline.push({
      $replaceRoot: { newRoot: '$ilkKayit' } // Gruplanmış kayıtlardan ilkini ana belge olarak al
    });
    // YENİ EKLENEN AŞAMA BİTTİ
    pipeline.push({ $sort: { 'veri.isim': 1, 'veri.soyIsim': 1 } });
    pipeline.push({ $skip: sayfa * sayfaBoyutu });
    pipeline.push({ $limit: sayfaBoyutu });
    // _id'yi string olarak da ekle (bag eslestirmesi icin)
    pipeline.push({ $addFields: { _idStr: { $toString: '$_id' } } });

    return pipeline;
  }

  /** Kisi ID listesinden isim/soyisim bilgilerini toplu cek (soyAgaciKisiler + goUyeler) */
  bagliKisilerPipelineOlustur(kisiIdleri: string[]): any[] {
    return [
      {
        $match: {
          vt: 'FikoYT.sistem.soyAgaciKisiler',
          silindi: false,
          _id: { $in: kisiIdleri.map(id => ({ $oid: id })) }
        }
      },
      {
        $project: {
          'veri.isim': 1,
          'veri.soyIsim': 1,
          'veri.cinsiyet': 1,
          'veri.platformKullaniciId': 1,
        }
      }
    ];
  }

  /** Yol adimlarini Turkce dilbilgisi kurallarina gore aciklamaya cevir
   *  Ornek: [BABA, ANNE] → "Babaanneniz"
   *  Ornek: [BABA, KIZKARDES, COCUK] → "Amcanızın Kızı" veya "Teyzenizin Oğlu"
   */
  yoluTurkceAcikla(yol: IAkrabalikAdim[], cinsiyetMap?: Map<string, string>): string {
    if (!yol || yol.length === 0) return '';

    if (!yol || yol.length === 0) return '';

    const bilgiler = yol.map(a => AkrabalikBilgi[a.akrabalikTuru]);

    // 1 adım: doğrudan sahipSiz
    if (yol.length === 1) {
      // Cinsiyete göre özel durumlar (örneğin KARDES için)
      const ilkAdimTuru = yol[0].akrabalikTuru;
      const ilkKisiCinsiyet = cinsiyetMap?.get(yol[0].kisiId);
      if (ilkAdimTuru === AkrabalikTuru.KARDES) {
        return ilkKisiCinsiyet === 'F' ? 'Kız Kardeşiniz' : 'Erkek Kardeşiniz';
      }
      return bilgiler[0]?.sahipSiz || bilgiler[0]?.isim || '';
    }

    // Path signature ile lookup
    const signature = yol.map(a => a.akrabalikTuru).join('+');
    const sonKisiCinsiyet = cinsiyetMap?.get(yol[yol.length - 1].kisiId);
    const kisaIsim = this.pathSignaturdenKisaIsim(signature, sonKisiCinsiyet);
    if (kisaIsim) {
      return kisaIsim;
    }

    // Fallback: chain mantığı
    const parcalar: string[] = [];

    // Ilk adim: 2. tekil sahiplik + tamlayan eki (Babanızın)
    const ilk = bilgiler[0];
    const ilkSahip = ilk?.sahipSiz || ilk?.isim || '';
    parcalar.push(this.sahipSizTamlayanEkle(ilkSahip));

    // Ortadaki adimlar: 3. tekil tamlayan (Annesinin, Babasının)
    for (let i = 1; i < yol.length - 1; i++) {
      parcalar.push(bilgiler[i]?.tamlayanO || bilgiler[i]?.isim || '');
    }

    // Son adim: 3. tekil sahiplik (Annesi, Babası, Kardeşi)
    const son = bilgiler[yol.length - 1];
    parcalar.push(son?.sahipO || son?.isim || '');

    return parcalar.join(' ');
  }

  /** Path signature'dan kısa isim döner, bulunamazsa null */
  private pathSignaturdenKisaIsim(signature: string, sonKisiCinsiyet?: string): string | null {
    switch (signature) {
      // 2-Adımlı Yollar
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.ANNE}`: return 'Babaanneniz';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.BABA}`: return 'Dedeniz (anne tarafından)';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.ANNE}`: return 'Anneanneniz';
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.BABA}`: return 'Dedeniz (baba tarafından)';

      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Halanız' : 'Amcanız';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Teyzeniz' : 'Dayınız';

      case `${AkrabalikTuru.ES}+${AkrabalikTuru.ANNE}`: return 'Kayınvalideniz';
      case `${AkrabalikTuru.ES}+${AkrabalikTuru.BABA}`: return 'Kayınpederiniz';
      case `${AkrabalikTuru.ES}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Baldızınız' : 'Kayınbiraderiniz';

      case `${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Torununuz';
      case `${AkrabalikTuru.COCUK}+${AkrabalikTuru.ES}`: return sonKisiCinsiyet === 'F' ? 'Gelininiz' : 'Damadınız';

      case `${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return 'Yeğeniniz';
      case `${AkrabalikTuru.KARDES}+${AkrabalikTuru.ES}`: return sonKisiCinsiyet === 'F' ? 'Yengeniz' : 'Enişteniz';

      case `${AkrabalikTuru.AMCA}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';
      case `${AkrabalikTuru.DAYI}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';
      case `${AkrabalikTuru.HALA}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';
      case `${AkrabalikTuru.TEYZE}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';

      case `${AkrabalikTuru.DEDE_BABA}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Büyük Halanız' : 'Büyük Amcanız';
      case `${AkrabalikTuru.NINE_BABA}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Büyük Teyzeniz' : 'Büyük Dayınız';
      case `${AkrabalikTuru.DEDE_ANNE}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Büyük Halanız' : 'Büyük Amcanız';
      case `${AkrabalikTuru.NINE_ANNE}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Büyük Teyzeniz' : 'Büyük Dayınız';

      // 3-Adımlı Yollar
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return 'Kuzeniniz';

      case `${AkrabalikTuru.KUZEN}+${AkrabalikTuru.COCUK}`: return 'Kuzenço';
      case `${AkrabalikTuru.YEGEN}+${AkrabalikTuru.COCUK}`: return 'Yeğenço';
      case `${AkrabalikTuru.TORUN}+${AkrabalikTuru.COCUK}`: return 'Torunço';

      case `${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Torunço';

      case `${AkrabalikTuru.ES}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Eşinizin Yeğeni (Kadın)' : 'Eşinizin Yeğeni (Erkek)';

      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.ANNE}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Babaannenizin Kız Kardeşi' : 'Babaannenizin Erkek Kardeşi';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.BABA}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Dedenizin Kız Kardeşi (anne tarafından)' : 'Dedenizin Erkek Kardeşi (anne tarafından)';

      // Eş Akrabalıkları (Yenge, Enişte, Kuzenin Eşi, vb.)
      case `${AkrabalikTuru.AMCA}+${AkrabalikTuru.ES}`: return 'Yengeniz';
      case `${AkrabalikTuru.DAYI}+${AkrabalikTuru.ES}`: return 'Yengeniz';
      case `${AkrabalikTuru.HALA}+${AkrabalikTuru.ES}`: return 'Enişteniz';
      case `${AkrabalikTuru.TEYZE}+${AkrabalikTuru.ES}`: return 'Enişteniz';

      case `${AkrabalikTuru.KUZEN}+${AkrabalikTuru.ES}`: return sonKisiCinsiyet === 'F' ? 'Kuzeninizin Eşi (Kadın)' : 'Kuzeninizin Eşi (Erkek)';
      case `${AkrabalikTuru.YEGEN}+${AkrabalikTuru.ES}`: return sonKisiCinsiyet === 'F' ? 'Yeğeninizin Eşi (Kadın)' : 'Yeğeninizin Eşi (Erkek)';
      case `${AkrabalikTuru.TORUN}+${AkrabalikTuru.ES}`: return sonKisiCinsiyet === 'F' ? 'Torununuzun Gelini' : 'Torununuzun Damadı';

      // 4+ Adımlı Yollar (daha az spesifik, genelleştirilmiş)
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Kuzeninizin Çocuğu (baba tarafından)';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Kuzeninizin Çocuğu (anne tarafından)';
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.BABA}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return 'Büyük Kuzeniniz (baba tarafından)';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.ANNE}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}`: return 'Büyük Kuzeniniz (anne tarafından)';
      case `${AkrabalikTuru.ES}+${AkrabalikTuru.KARDES}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Eşinizin Yeğeninin Çocuğu';

      // Baba / Anne üzerinden torun (dolaylı)
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Torununuz';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`: return 'Torununuz';

      // Diğerleri - Akrabalık Ağacının daha derinlerine inildikçe, daha genel ifadeler kullanılıyor
      case `${AkrabalikTuru.COCUK}+${AkrabalikTuru.ES}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Gelininizin Çocuğu' : 'Damadınızın Çocuğu';

      // Dede / Nine üzerinden akrabalıklar
      case `${AkrabalikTuru.DEDE_BABA}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Büyük Halanız' : 'Büyük Amcanız';
      case `${AkrabalikTuru.NINE_BABA}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Büyük Teyzeniz' : 'Büyük Dayınız';
      case `${AkrabalikTuru.DEDE_ANNE}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Büyük Halanız' : 'Büyük Amcanız';
      case `${AkrabalikTuru.NINE_ANNE}+${AkrabalikTuru.KARDES}`: return sonKisiCinsiyet === 'F' ? 'Büyük Teyzeniz' : 'Büyük Dayınız';

      // Tekrar eden veya daha spesifik haller
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.ANNE}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Halanız' : 'Amcanız';
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Kız Kardeşiniz' : 'Erkek Kardeşiniz'; // Annenizin çocuğu = kardeş
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.COCUK}`: return sonKisiCinsiyet === 'F' ? 'Kız Kardeşiniz' : 'Erkek Kardeşiniz'; // Babanızın çocuğu = kardeş

      // Uzun zincirler için daha genel kısaltmalar
      // BABA + ANNE + COCUK + COCUK + COCUK -> Amcanız/Halanızın Torunu
      case `${AkrabalikTuru.BABA}+${AkrabalikTuru.ANNE}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`:
      case `${AkrabalikTuru.ANNE}+${AkrabalikTuru.BABA}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}+${AkrabalikTuru.COCUK}`:
        return 'Kuzenço';



      default: return null;
    }
  }

  /** "Babanız" → "Babanızın", "Anneniz" → "Annenizin" vb. */
  private sahipSizTamlayanEkle(sahipSiz: string): string {
    if (sahipSiz.endsWith('nız')) return sahipSiz + 'ın';
    if (sahipSiz.endsWith('niz')) return sahipSiz + 'in';
    if (sahipSiz.endsWith('nuz')) return sahipSiz + 'un';
    if (sahipSiz.endsWith('nüz')) return sahipSiz + 'ün';
    // Özel durumlar (yengeniz, enişteniz gibi kelimeler tamlayan eki almaz)
    if (sahipSiz.includes('Yengeniz') || sahipSiz.includes('Enişteniz') || sahipSiz.includes('Kuzeniniz')) return sahipSiz;
    return sahipSiz + 'ın'; // fallback
  }

  /** Iki kisi arasindaki TUM akrabalik yollarini bul (en kisa + alternatifler) */
  tumAkrabalikYollariBul(baglar: any[], kisi1Id: string, kisi2Id: string, cinsiyetMap?: Map<string, string>): IAkrabalikYolu[] {
    const komsuListe = this.komsuListeOlustur(baglar, cinsiyetMap);

    // Once BFS ile en kisa yolu bul
    const enKisa = this.bfsEnKisaYol(komsuListe, kisi1Id, kisi2Id);
    if (!enKisa) return [];

    const maxDerinlik = Math.min(enKisa.length + 2, 8);

    // DFS ile tum yollari bul (maxDerinlik sinirli)
    const tumYollar: IAkrabalikAdim[][] = [];
    const ziyaretEdilen = new Set<string>([kisi1Id]);

    const dfs = (mevcutId: string, yol: IAkrabalikAdim[]) => {
      if (yol.length > maxDerinlik) return;
      if (tumYollar.length >= 10) return; // performans siniri
      if (mevcutId === kisi2Id) {
        tumYollar.push([...yol]);
        return;
      }
      for (const komsuluk of (komsuListe.get(mevcutId) ?? [])) {
        if (!ziyaretEdilen.has(komsuluk.komsu)) {
          ziyaretEdilen.add(komsuluk.komsu);
          yol.push({ kisiId: komsuluk.komsu, akrabalikTuru: komsuluk.tur });
          dfs(komsuluk.komsu, yol);
          yol.pop();
          ziyaretEdilen.delete(komsuluk.komsu);
        }
      }
    };

    dfs(kisi1Id, []);

    // Aciklama olustur, sirala ve benzersizlestir
    const sonuclar = tumYollar.map(yol => ({
      kisi1: null,
      kisi2: null,
      yol: [...yol],
      mesafe: yol.length,
      aciklama: this.yoluTurkceAcikla(yol, cinsiyetMap)
    }));

    sonuclar.sort((a, b) => a.mesafe - b.mesafe);

    const gorulen = new Set<string>();
    return sonuclar.filter(s => {
      if (gorulen.has(s.aciklama)) return false;
      gorulen.add(s.aciklama);
      return true;
    }).slice(0, 5);
  }

  /** Komsu listesi olustur (BFS/DFS icin paylasilir) */
  private komsuListeOlustur(baglar: any[], cinsiyetMap?: Map<string, string>): Map<string, { komsu: string; tur: AkrabalikTuru }[]> {
    const komsuListe = new Map<string, { komsu: string; tur: AkrabalikTuru }[]>();
    for (const bag of baglar) {
      const v = bag.veri || bag;
      if (!v.kisi1 || !v.kisi2) continue;
      const k1 = String(v.kisi1);
      const k2 = String(v.kisi2);
      if (!komsuListe.has(k1)) komsuListe.set(k1, []);
      if (!komsuListe.has(k2)) komsuListe.set(k2, []);
      komsuListe.get(k1).push({ komsu: k2, tur: v.akrabalikTuru });
      // Cinsiyet bazlı ters ilişki öncelikli (DB'deki tersAkrabalikTuru yanlış olabilir)
      const cinsiyetTers = this.cinsiyetleTersCevir(v.akrabalikTuru, cinsiyetMap?.get(k1));
      let tersTur = cinsiyetTers || v.tersAkrabalikTuru || AkrabalikBilgi[v.akrabalikTuru]?.tersi;
      if (tersTur) {
        komsuListe.get(k2).push({ komsu: k1, tur: tersTur });
      }
    }
    return komsuListe;
  }

  /** BFS ile en kisa yolu bul (internal) */
  private bfsEnKisaYol(komsuListe: Map<string, { komsu: string; tur: AkrabalikTuru }[]>, kisi1Id: string, kisi2Id: string): IAkrabalikAdim[] | null {
    const kuyruk: { id: string; yol: IAkrabalikAdim[] }[] = [{ id: kisi1Id, yol: [] }];
    const ziyaretEdilen = new Set<string>([kisi1Id]);
    while (kuyruk.length > 0) {
      const mevcut = kuyruk.shift()!;
      if (mevcut.id === kisi2Id) return mevcut.yol;
      for (const komsuluk of (komsuListe.get(mevcut.id) ?? [])) {
        if (!ziyaretEdilen.has(komsuluk.komsu)) {
          ziyaretEdilen.add(komsuluk.komsu);
          kuyruk.push({
            id: komsuluk.komsu,
            yol: [...mevcut.yol, { kisiId: komsuluk.komsu, akrabalikTuru: komsuluk.tur }]
          });
        }
      }
    }
    return null;
  }

  /** Aile istatistiklerini hesapla (client-side, yuklenmis chart verilerinden) */
  istatistikHesapla(veriler: Map<string, IFamilyChartDugum>): IAileIstatistikleri {
    let erkek = 0, kadin = 0, yasayan = 0, vefat = 0;
    const isimSayaci = new Map<string, number>();
    const sehirSayaci = new Map<string, number>();
    const meslekSayaci = new Map<string, number>();
    let maxDerinlik = 0;

    // Yas hesaplamasi icin
    let enYasliKisi: { isim: string; yas: number } | undefined;
    let enGencKisi: { isim: string; yas: number } | undefined;
    const yaslar: number[] = [];
    const vefatYaslari: number[] = [];

    for (const [, dugum] of veriler) {
      const d = dugum.data;
      if (d.gender === 'M') erkek++; else kadin++;
      if (d.vefatEtti) vefat++; else yasayan++;

      const isim = d['first name']?.trim();
      if (isim && isim !== 'Bilinmiyor') {
        isimSayaci.set(isim, (isimSayaci.get(isim) || 0) + 1);
      }

      const yer = d.dogumYeri?.trim();
      if (yer) {
        sehirSayaci.set(yer, (sehirSayaci.get(yer) || 0) + 1);
      }

      const meslek = d.meslek?.trim();
      if (meslek) {
        meslekSayaci.set(meslek, (meslekSayaci.get(meslek) || 0) + 1);
      }

      // Yas hesapla
      if (d.birthday) {
        const tamIsim = `${d['first name'] || ''} ${d['last name'] || ''}`.trim() || 'Bilinmiyor';
        if (d.vefatEtti && d.olumTarihi) {
          const vefatYas = this.tarihFarkHesapla(d.birthday, d.olumTarihi);
          if (vefatYas !== null && vefatYas >= 0) {
            vefatYaslari.push(vefatYas);
            yaslar.push(vefatYas);
            if (!enYasliKisi || vefatYas > enYasliKisi.yas) {
              enYasliKisi = { isim: tamIsim, yas: vefatYas };
            }
            if (!enGencKisi || vefatYas < enGencKisi.yas) {
              enGencKisi = { isim: tamIsim, yas: vefatYas };
            }
          }
        } else {
          const yas = this.tarihFarkHesapla(d.birthday);
          if (yas !== null && yas >= 0) {
            yaslar.push(yas);
            if (!enYasliKisi || yas > enYasliKisi.yas) {
              enYasliKisi = { isim: tamIsim, yas };
            }
            if (!enGencKisi || yas < enGencKisi.yas) {
              enGencKisi = { isim: tamIsim, yas };
            }
          }
        }
      }

      // Nesil derinligi - ebeveynleri takip et
      let derinlik = 0;
      let mevcut = dugum;
      while (mevcut.rels.parents?.length && derinlik < 20) {
        const ebeveynId = mevcut.rels.parents[0];
        const ebeveyn = veriler.get(ebeveynId);
        if (!ebeveyn) break;
        mevcut = ebeveyn;
        derinlik++;
      }
      if (derinlik > maxDerinlik) maxDerinlik = derinlik;
    }

    const sirala = (map: Map<string, number>) =>
      Array.from(map.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([isim, sayi]) => ({ isim, sayi }));

    const sehirSirali = Array.from(sehirSayaci.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([sehir, sayi]) => ({ sehir, sayi }));

    const meslekSirali = Array.from(meslekSayaci.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([meslek, sayi]) => ({ meslek, sayi }));

    const ortalamaYasam = yaslar.length > 0
      ? Math.round(yaslar.reduce((t, y) => t + y, 0) / yaslar.length)
      : undefined;

    const ortalamaVefatYasi = vefatYaslari.length > 0
      ? Math.round(vefatYaslari.reduce((t, y) => t + y, 0) / vefatYaslari.length)
      : undefined;

    return {
      toplamUye: veriler.size,
      nesilSayisi: maxDerinlik + 1,
      cinsiyetDagilimi: { erkek, kadin },
      yasayanSayisi: yasayan,
      vefatSayisi: vefat,
      ortalamaYasam,
      ortalamaVefatYasi,
      enYasliKisi,
      enGencKisi,
      enCokIsim: sirala(isimSayaci),
      sehirDagilimi: sehirSirali,
      meslekDagilimi: meslekSirali,
    };
  }

  /** Aile zaman cizelgesi olustur (client-side) */
  zamanCizelgesiOlustur(veriler: Map<string, IFamilyChartDugum>): IZamanCizelgesiOlayi[] {
    const olaylar: IZamanCizelgesiOlayi[] = [];

    for (const [, dugum] of veriler) {
      const d = dugum.data;
      const isim = `${d['first name'] || ''} ${d['last name'] || ''}`.trim() || 'Bilinmiyor';

      if (d.birthday) {
        olaylar.push({
          tarih: d.birthday,
          baslik: `${isim} dogdu`,
          turu: YasamOlayiTuru.DOGUM,
          kisiId: dugum.id,
          kisiIsim: isim,
          konum: d.dogumYeri,
          renk: '#10b981',
        });
      }

      if (d.vefatEtti && d.olumTarihi) {
        olaylar.push({
          tarih: d.olumTarihi,
          baslik: `${isim} vefat etti`,
          turu: YasamOlayiTuru.VEFAT,
          kisiId: dugum.id,
          kisiIsim: isim,
          konum: d.olumYeri,
          renk: '#64748b',
        });
      }
    }

    // Tarihe gore sirala (en yeni basta)
    olaylar.sort((a, b) => {
      const ta = this.tarihParse(a.tarih);
      const tb = this.tarihParse(b.tarih);
      return tb - ta;
    });

    return olaylar;
  }

  private tarihParse(tarih: string): number {
    if (!tarih) return 0;
    // dd.mm.yyyy veya yyyy formatini destekle
    const parcalar = tarih.split('.');
    if (parcalar.length === 3) {
      return new Date(`${parcalar[2]}-${parcalar[1]}-${parcalar[0]}`).getTime() || 0;
    }
    return new Date(tarih).getTime() || 0;
  }

  // =====================================================
  // KISI KAYIT SISTEMI - Bagimsiz kisi kayitlari
  // =====================================================

  /** Yeni kisi kaydi olustur, MongoDB'den alinan _id'yi dondur */
  async kisiKaydiOlustur(veri: ISoyAgaciKisiVeri): Promise<string> {
    const kart = {
      vt: 'FikoYT.sistem.soyAgaciKisiler',
      yol: 'FikoYT.sistem.soyAgaciKisiler.kalem',
      turu: IKartTuru.SISTEM,
      sahip: this.FikoYT.kimlik.aktifKullaniciJson()?._id || '',
      silindi: false,
      tarih: new Date(),
      veri,
    };

    const istek = new FikoYTIstekKok({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.ekle',
      onEkOlsun: true,
      depoModu: false,
      oncelik: 5,
    });

    const formData = new FormData();
    formData.append('req_body', JSON.stringify(kart));
    istek.parametreler = formData;
    await istek.isteSync();

    // Backend UpsertedID veya sonuc'tan _id'yi al
    const sonuc = istek.cevap?.sonuc;
    let yeniId: string | null = null;

    if (Array.isArray(sonuc)) {
      // EkleYadaGuncelle dondurur: [{UpsertedID: "..."}]
      for (const r of sonuc) {
        if (r?.UpsertedID) { yeniId = r.UpsertedID; break; }
      }
    } else if (sonuc?.UpsertedID) {
      yeniId = sonuc.UpsertedID;
    } else if (sonuc?.InsertedID) {
      yeniId = sonuc.InsertedID;
    }

    if (!yeniId) {
      console.warn('[SoyAgaci] kisiKaydiOlustur: UpsertedID alinamadi, sonuc:', sonuc);
      throw new Error('Kisi kaydi olusturuldu ama ID alinamadi');
    }

    console.log('[SoyAgaci] Kisi kaydi olusturuldu:', yeniId, veri.isim, veri.soyIsim);
    return yeniId;
  }

  /** Kisi kaydini guncelle (dot-notation set) */
  async kisiKaydiGuncelle(kisiId: string, set: Record<string, any>): Promise<void> {
    await this.guncelleSorgu({ _id: kisiId }, set);
    this.cachedenSil(kisiId);
  }

  /** Kisi kaydini sil (silindi: true) */
  async kisiKaydiSil(kisiId: string): Promise<void> {
    await this.guncelleSorgu({ _id: kisiId }, { silindi: true });
    this.cachedenSil(kisiId);
  }

  /** Yeni bag kaydi olustur (sadece kisi referanslari, veri yok) */
  async bagKaydiOlustur(kisi1: string, kisi2: string, akrabalikTuru: AkrabalikTuru, tersAkrabalikTuru?: AkrabalikTuru): Promise<string> {
    const kart = {
      vt: 'FikoYT.sistem.aileBaglari',
      yol: 'FikoYT.sistem.aileBaglari.kalem',
      turu: IKartTuru.SISTEM,
      sahip: this.FikoYT.kimlik.aktifKullaniciJson()?._id || '',
      silindi: false,
      tarih: new Date(),
      veri: {
        kisi1,
        kisi2,
        akrabalikTuru,
        tersAkrabalikTuru: tersAkrabalikTuru ?? AkrabalikBilgi[akrabalikTuru]?.tersi,
        onayDurumu: AkrabalikOnayDurumu.ONAYLANDI,
        notlar: '',
      }
    };

    const istek = new FikoYTIstekKok({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.ekle',
      onEkOlsun: true,
      depoModu: false,
      oncelik: 5,
    });

    const formData = new FormData();
    formData.append('req_body', JSON.stringify(kart));
    istek.parametreler = formData;
    await istek.isteSync();

    const sonuc = istek.cevap?.sonuc;
    let yeniId = '';
    if (Array.isArray(sonuc)) {
      for (const r of sonuc) { if (r?.UpsertedID) { yeniId = r.UpsertedID; break; } }
    } else if (sonuc?.UpsertedID) {
      yeniId = sonuc.UpsertedID;
    }

    console.log('[SoyAgaci] Bag kaydi olusturuldu:', yeniId, akrabalikTuru, kisi1, '->', kisi2);
    // Yeni bağ eklendiyse ilgili kişilerin cache'ini temizle
    this.cachedenSil(kisi1);
    this.cachedenSil(kisi2);
    return yeniId;
  }

  /** Kisi kayitlarini ID'lerine gore cek */
  kisiKayitlariPipelineOlustur(kisiIdleri: string[]): any[] {
    return [
      {
        $match: {
          vt: 'FikoYT.sistem.soyAgaciKisiler',
          silindi: false,
          $or: [
            { _id: { $in: kisiIdleri } },
            { 'veri.platformKullaniciId': { $in: kisiIdleri } }
          ]
        }
      }
    ];
  }

  /** Belirli kullanicinin tum kisi kayitlarini cek */
  tumKisiKayitlariPipelineOlustur(sahipId: string): any[] {
    return [
      {
        $match: {
          vt: 'FikoYT.sistem.soyAgaciKisiler',
          silindi: false,
          sahip: sahipId,
        }
      }
    ];
  }

  /** Merkezi: Bir kişinin detayını ve ilişkilerini çek (balon, kişi sayfası vb. için) */
  async kisiDetayVeIliskilerCek(kisiId: string): Promise<{ kisiDetay: any; iliskiler: { karsiId: string; tur: string; simge: string; renk: string; isim: string; avatarUrl: string | null; basHarf: string; cinsiyet: string }[] }> {
    const cached = this.uc.al<any>(`${this.CACHE_ONEK}${kisiId}`, this.CACHE_TTL);
    if (cached) return cached;

    const fapiUrl = this.FikoYT?.env?.adres?.fapiUrl || '';

    // 1) Kişi detayını çek (hem _id hem platformKullaniciId ile ara)
    const kisiSonuc = await this.aggregateSorgu([
      { $match: { vt: 'FikoYT.sistem.soyAgaciKisiler', silindi: { $ne: true }, $or: [
        { _id: kisiId },
        { 'veri.platformKullaniciId': kisiId },
      ] } },
      { $limit: 1 },
    ]);
    const kisiKayit = kisiSonuc[0];
    const kisiVeri = kisiKayit?.veri || {};
    const gercekKisiId = kisiKayit?._id || kisiId;

    // Alt ID: hem platformKullaniciId hem de gerçek _id
    const altId = kisiVeri.platformKullaniciId || '';
    // Tüm olası ID'leri topla
    const tumIdler = new Set<string>([String(kisiId), String(gercekKisiId)]);
    if (altId) tumIdler.add(String(altId));

    // 2) Doğrudan bağları çek (tüm olası ID'lerle)
    const idListesi = Array.from(tumIdler);
    const dogruBaglar = await this.aggregateSorgu([
      { $match: { vt: 'FikoYT.sistem.aileBaglari', silindi: { $ne: true }, $or: [
        { 'veri.kisi1': idListesi.length > 1 ? { $in: idListesi } : idListesi[0] },
        { 'veri.kisi2': idListesi.length > 1 ? { $in: idListesi } : idListesi[0] },
      ] } },
      { $limit: 100 },
    ]);

    // 3) Doğrudan bağlardan karşı kişileri ve rollerini belirle
    const gorulen = new Set<string>();
    // tumIdler'deki herhangi birini "görüldü" say
    for (const id of tumIdler) gorulen.add(id);
    const ham: { karsiId: string; tur: AkrabalikTuru; bag: any }[] = [];
    const ebeveynIdler: string[] = [];  // Kişinin ebeveynleri (kardeş bulmak için)
    const esIdler: string[] = [];       // Kişinin eşleri (dolaylı çocuk bulmak için)

    for (const b of dogruBaglar) {
      const v = b.veri || {};
      const k1 = String(v.kisi1 || ''), k2 = String(v.kisi2 || '');
      const k1Ben = tumIdler.has(k1);
      const k2Ben = tumIdler.has(k2);
      if (!k1Ben && !k2Ben) continue;
      const karsiId = k1Ben ? k2 : k1;
      const eslesenId = k1Ben ? k1 : k2;
      if (!karsiId || gorulen.has(karsiId)) continue;
      gorulen.add(karsiId);

      // Tür belirle (cinsiyet sonra düzeltilecek, şimdilik ham tür)
      const tur = bagdanTurCozumle(eslesenId, b);
      ham.push({ karsiId, tur, bag: b });

      // Ebeveyn ve eş ID'lerini topla (2. seviye sorgu için)
      if (tur === AkrabalikTuru.ANNE || tur === AkrabalikTuru.BABA) {
        ebeveynIdler.push(karsiId);
      } else if (tur === AkrabalikTuru.ES) {
        esIdler.push(karsiId);
      }
    }

    // 4) 2. seviye: Ebeveynlerin + eşlerin bağlarını çek
    const ikinciSeviyeIdler = [...ebeveynIdler, ...esIdler];
    if (ikinciSeviyeIdler.length > 0) {
      const ekBaglar = await this.aggregateSorgu([
        { $match: { vt: 'FikoYT.sistem.aileBaglari', silindi: { $ne: true }, $or: [
          { 'veri.kisi1': { $in: ikinciSeviyeIdler } },
          { 'veri.kisi2': { $in: ikinciSeviyeIdler } },
        ] } },
        { $limit: 200 },
      ]);

      const ebeveynSet = new Set(ebeveynIdler);
      const esSet = new Set(esIdler);
      const digerEbeveynIdler: string[] = []; // ebeveynin eşleri (diğer ebeveyn)

      // İlk geçiş: ebeveynin eşlerini bul (= diğer ebeveyn) + doğrudan çocuk/kardeş
      for (const b of ekBaglar) {
        const v = b.veri || {};
        const k1 = String(v.kisi1 || ''), k2 = String(v.kisi2 || '');
        const tur = String(v.akrabalikTuru || '');
        const tersTur = String(v.tersAkrabalikTuru || AkrabalikBilgi[tur as AkrabalikTuru]?.tersi || '');

        // Ebeveynin ES bağı → diğer ebeveyn (bag: null → step 6 cinsiyet düzeltir)
        if (ebeveynSet.has(k1) && tur === AkrabalikTuru.ES && !gorulen.has(k2) && !tumIdler.has(k2)) {
          gorulen.add(k2);
          ham.push({ karsiId: k2, tur: AkrabalikTuru.ANNE, bag: null });
          digerEbeveynIdler.push(k2);
        } else if (ebeveynSet.has(k2) && (tur === AkrabalikTuru.ES || tersTur === AkrabalikTuru.ES) && !gorulen.has(k1) && !tumIdler.has(k1)) {
          gorulen.add(k1);
          ham.push({ karsiId: k1, tur: AkrabalikTuru.ANNE, bag: null });
          digerEbeveynIdler.push(k1);
        }

        // Ebeveynin COCUK bağı → kardeş
        this._ekBagIsle(b, ebeveynSet, gorulen, tumIdler, ham, AkrabalikTuru.KARDES);
        // Eşin COCUK bağı → çocuk
        this._ekBagIsle(b, esSet, gorulen, tumIdler, ham, AkrabalikTuru.COCUK);
      }

      // İkinci geçiş: diğer ebeveynlerin çocuklarını bul (= daha fazla kardeş)
      if (digerEbeveynIdler.length > 0) {
        const digerBaglar = await this.aggregateSorgu([
          { $match: { vt: 'FikoYT.sistem.aileBaglari', silindi: { $ne: true }, $or: [
            { 'veri.kisi1': { $in: digerEbeveynIdler } },
            { 'veri.kisi2': { $in: digerEbeveynIdler } },
          ] } },
          { $limit: 200 },
        ]);
        const digerEbeveynSet = new Set(digerEbeveynIdler);
        for (const b of digerBaglar) {
          this._ekBagIsle(b, digerEbeveynSet, gorulen, tumIdler, ham, AkrabalikTuru.KARDES);
        }
      }
    }

    if (!ham.length) {
      const bossonuc = { kisiDetay: kisiVeri, iliskiler: [] };
      this.uc.set(`${this.CACHE_ONEK}${kisiId}`, bossonuc, this.CACHE_TTL);
      return bossonuc;
    }

    // 5) Tüm karşı kişi bilgilerini toplu çek
    const karsiIdler = ham.map(h => h.karsiId);
    const kisiSonuclari = await this.aggregateSorgu([
      { $match: { vt: 'FikoYT.sistem.soyAgaciKisiler', silindi: { $ne: true }, $or: [
        { _id: { $in: karsiIdler } },
        { 'veri.platformKullaniciId': { $in: karsiIdler } }
      ] } },
      { $project: { _id: 1, 'veri.isim': 1, 'veri.soyisim': 1, 'veri.soyIsim': 1, 'veri.cinsiyet': 1, 'veri.resimUrl': 1, 'veri.platformKullaniciId': 1 } }
    ]);
    const kisiler: Record<string, any> = {};
    for (const k of kisiSonuclari) {
      const v = k.veri || {};
      kisiler[k._id] = v;
      if (v.platformKullaniciId) kisiler[v.platformKullaniciId] = v;
    }

    // 6) Sonuçları oluştur
    const iliskiler = ham.map(h => {
      const karsi = kisiler[h.karsiId] || {};
      const kCinsiyet = karsi.cinsiyet || '';
      // Doğrudan bağ varsa bagdanTurCozumle kullan, dolaylı ise ham.tur kullan
      let tur = h.tur;
      if (h.bag && !([AkrabalikTuru.KARDES, AkrabalikTuru.COCUK].includes(h.tur) && !h.bag.veri?.kisi1)) {
        // Doğrudan bağ: cinsiyet bazlı düzeltme uygula
        const eslesenId = tumIdler.has(String(h.bag.veri?.kisi1 || '')) ? String(h.bag.veri.kisi1) : String(h.bag.veri?.kisi2 || '');
        if (tumIdler.has(eslesenId)) {
          tur = bagdanTurCozumle(eslesenId, h.bag, kCinsiyet);
        }
      }
      // Dolaylı ilişkilerde (bag yok) cinsiyet bazlı ebeveyn türü düzeltme
      if (!h.bag && (tur === AkrabalikTuru.ANNE || tur === AkrabalikTuru.BABA) && kCinsiyet) {
        tur = (kCinsiyet === 'F' || kCinsiyet === 'K') ? AkrabalikTuru.ANNE : AkrabalikTuru.BABA;
      }
      const bilgi = AkrabalikBilgi[tur];
      if (!bilgi) return null;
      const isim = [karsi.isim, karsi.soyisim || karsi.soyIsim].filter(Boolean).join(' ') || '?';
      let avatarUrl = karsi.resimUrl || '';
      if (!avatarUrl && karsi.platformKullaniciId && fapiUrl) {
        avatarUrl = `${fapiUrl}/fapiv1/fikoyt.com.FikoYT.sistem.dosyalar.${karsi.platformKullaniciId}.resim?boyut=80x80&thumbnail=true`;
      }
      return {
        karsiId: h.karsiId, tur: bilgi.isim, simge: bilgi.simge, renk: bilgi.renk,
        isim, avatarUrl: avatarUrl || null, basHarf: (isim[0] || '?').toUpperCase(), cinsiyet: kCinsiyet
      };
    }).filter(Boolean);

    const sonuc = { kisiDetay: kisiVeri, iliskiler };
    this.uc.set(`${this.CACHE_ONEK}${kisiId}`, sonuc, this.CACHE_TTL);
    return sonuc;
  }

  /** Platform kullanıcı ID'sine göre soyağacı kişi _id'sini bul */
  async soyagaciKisiIdBulByPlatformId(platformId: string): Promise<string | null> {
    const sonuc = await this.aggregateSorgu([
      { $match: { vt: 'FikoYT.sistem.soyAgaciKisiler', silindi: { $ne: true }, 'veri.platformKullaniciId': platformId } },
      { $limit: 1 },
      { $project: { _id: 1 } }
    ]);
    return sonuc[0]?._id || null;
  }

  /** İsme göre soyağacı kişi _id'sini bul (tam eşleşme: isim + soyisim) */
  async soyagaciKisiIdBulByIsim(tamIsim: string): Promise<string | null> {
    const parcalar = tamIsim.trim().split(/\s+/);
    if (parcalar.length < 2) return null;
    const soyisim = parcalar.pop()!;
    const isim = parcalar.join(' ');
    const sonuc = await this.aggregateSorgu([
      { $match: {
        vt: 'FikoYT.sistem.soyAgaciKisiler', silindi: { $ne: true },
        $or: [
          { 'veri.isim': { $regex: escapeRegex(isim), $options: 'i' }, 'veri.soyIsim': { $regex: escapeRegex(soyisim), $options: 'i' } },
          { 'veri.isim': { $regex: escapeRegex(isim), $options: 'i' }, 'veri.soyisim': { $regex: escapeRegex(soyisim), $options: 'i' } },
        ]
      } },
      { $limit: 1 },
      { $project: { _id: 1 } }
    ]);
    return sonuc[0]?._id || null;
  }

  /** Cinsiyet bazli ters akrabalik turu belirle - merkezi fonksiyonu kullanir */
  private cinsiyetleTersCevir(tur: AkrabalikTuru, cinsiyet: string | undefined): AkrabalikTuru | null {
    return cinsiyetleTurDuzelt(tur, cinsiyet);
  }

  // =====================================================
  // MERKEZI UTILITY FONKSIYONLARI
  // =====================================================

  /** Tarih string'ini Date objesine çevir (DD.MM.YYYY veya YYYY-MM-DD) */
  parseDateString(dateString: string): Date | null {
    if (!dateString) return null;
    const parts = dateString.split('.');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(dateString);
  }

  /** İki tarih arasındaki yıl farkını hesapla (yaş hesaplama) */
  tarihFarkHesapla(baslangicTarihiStr: string, bitisTarihiStr?: string): number | null {
    const baslangic = this.parseDateString(baslangicTarihiStr);
    if (!baslangic || isNaN(baslangic.getTime())) return null;
    const bitis = bitisTarihiStr ? this.parseDateString(bitisTarihiStr) : new Date();
    if (!bitis || isNaN(bitis.getTime())) return null;
    let fark = bitis.getFullYear() - baslangic.getFullYear();
    const ayFarki = bitis.getMonth() - baslangic.getMonth();
    if (ayFarki < 0 || (ayFarki === 0 && bitis.getDate() < baslangic.getDate())) {
      fark--;
    }
    return fark >= 0 ? fark : null;
  }

  /** Kişinin tüm olası ID'lerini döner (_id + platformKullaniciId), kisiKayitlariMap opsiyonel */
  kisiAltIdleri(kisiId: string, kisiKayitlariMap?: Map<string, any>): Set<string> {
    const ids = new Set<string>([String(kisiId)]);
    let kaydi = kisiKayitlariMap?.get(kisiId);
    if (!kaydi && kisiKayitlariMap) {
      for (const [, k] of kisiKayitlariMap) {
        const v = k?.veri || k;
        if (String(v?.platformKullaniciId) === String(kisiId) || String(k?._id) === String(kisiId)) {
          kaydi = k; break;
        }
      }
    }
    if (kaydi?._id) ids.add(String(kaydi._id));
    if (kaydi?.veri?.platformKullaniciId) ids.add(String(kaydi.veri.platformKullaniciId));
    else if ((kaydi as any)?.platformKullaniciId) ids.add(String((kaydi as any).platformKullaniciId));
    return ids;
  }

  /** İsmin baş harflerini al ("Ahmet Yılmaz" → "AY") */
  basHarfAl(isim?: string, soyIsim?: string): string {
    if (isim && soyIsim) {
      return ((isim.charAt(0) || '') + (soyIsim.charAt(0) || '')).toUpperCase() || '?';
    }
    // Tek string'den (ad soyad birleşik)
    const parts = (isim || '').trim().split(' ');
    let initials = '';
    if (parts.length > 0 && parts[0].length > 0) initials += parts[0][0];
    if (parts.length > 1 && parts[1].length > 0) initials += parts[1][0];
    return initials.toUpperCase() || '?';
  }

  /** Cinsiyet bazlı avatar arka plan rengi */
  avatarRenk(cinsiyet?: string): string {
    return cinsiyet === 'K' || cinsiyet === 'F' ? '#ec4899' : '#3b82f6';
  }

  /**
   * 2. seviye bağ işleme: kaynakSet'teki kişilerin COCUK bağlarını bul,
   * karşı tarafı hedefTur ile ham'a ekle (kardeş veya çocuk olarak).
   */
  private _ekBagIsle(
    b: any,
    kaynakSet: Set<string>,
    gorulen: Set<string>,
    tumIdler: Set<string>,
    ham: { karsiId: string; tur: AkrabalikTuru; bag: any }[],
    hedefTur: AkrabalikTuru
  ): void {
    const v = b.veri || {};
    const k1 = String(v.kisi1 || ''), k2 = String(v.kisi2 || '');
    const tur = v.akrabalikTuru as AkrabalikTuru;

    // Case 1: kisi1 kaynakSet'te (ebeveyn/eş), akrabalikTuru=COCUK → kisi2 çocuk
    if (kaynakSet.has(k1) && tur === AkrabalikTuru.COCUK && !gorulen.has(k2) && !tumIdler.has(k2)) {
      gorulen.add(k2);
      ham.push({ karsiId: k2, tur: hedefTur, bag: null });
    }

    // Case 2: kisi2 kaynakSet'te, tur=BABA/ANNE → kisi2 ebeveyn, kisi1 çocuk
    if (kaynakSet.has(k2) && !gorulen.has(k1) && !tumIdler.has(k1)) {
      const tersTur = (v.tersAkrabalikTuru || AkrabalikBilgi[tur]?.tersi) as AkrabalikTuru;
      if (tur === AkrabalikTuru.BABA || tur === AkrabalikTuru.ANNE || tersTur === AkrabalikTuru.COCUK) {
        gorulen.add(k1);
        ham.push({ karsiId: k1, tur: hedefTur, bag: null });
      }
    }
  }
}
